import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import Modal from "./Modal";
import "../styles/KpiFormModal.css";
import "../styles/Modal.css";

export default function InputKPIModal({ user, onClose }) {
  const API = process.env.REACT_APP_API_BASE_URL || "";

  const [indikatorOpen, setIndikatorOpen] = useState(false);
  const [submittedIndicators, setSubmittedIndicators] = useState([]);
  const [indikatorData, setIndikatorData] = useState([]);
  const [form, setForm] = useState({
    nama: "",
    divisi: "",
    unit: "",
    area_kinerja: "",
    parameter: "",
    sumber: "",
    indikator_kpi: "",
    target: "",
    bobot: "",
    nilai: "",
    actual: "",
  });

  // ===== TARGET CONTROL =====
  const [editTarget, setEditTarget] = useState(false);
  const [isTargetFluktuatif, setIsTargetFluktuatif] = useState(false);

  // ===== FILE STATES =====
  const [buktiNilaiPreview, setBuktiNilaiPreview] = useState(null);
  const [buktiNilaiName, setBuktiNilaiName] = useState("");
  const [buktiNilaiFile, setBuktiNilaiFile] = useState(null);
  const [buktiIsPDF, setBuktiIsPDF] = useState(false);

  const [drawing, setDrawing] = useState("");
  const canvasRef = useRef(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [filteredIndicators, setFilteredIndicators] = useState([]);
  const [batchList, setBatchList] = useState([]);

  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const namaDropdownRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [loadingText, setLoadingText] = useState("Memproses...");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("info");

  const showModal = (title, message, type = "info") => {
    setModalTitle(title);
    setModalMessage(message);
    setModalType(type);
    setModalOpen(true);
  };

  const showFinishModal = () => {
    setModalTitle("Selesai");
    setModalMessage(
      "Terima kasih telah mengisi KPI. Pengisian KPI berhasil disimpan.",
    );
    setModalType("finish");
    setModalOpen(true);
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (loading) {
        e.preventDefault();
        e.returnValue =
          "Data KPI sedang disimpan. Apakah Anda yakin ingin meninggalkan halaman?";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [loading]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        namaDropdownRef.current &&
        !namaDropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🔥 PERBAIKAN 1: LOAD MASTER INDIKATOR (ANTI-CRASH FORMAT) 🔥
  useEffect(() => {
    axios
      .get(`${API}/api/indikator-data`)
      .then((res) => {
        if (res.data.result === "success") {
          // Ambil data dari data atau message (tergantung balikan API)
          const fetchedData = res.data.data || res.data.message;
          let finalData = [];

          if (Array.isArray(fetchedData)) {
            finalData = fetchedData;
          } else if (typeof res.data === "object") {
            // Jika format array rusak dari Google Script, kita ekstrak manual
            finalData = Object.values(res.data).filter(
              (v) => typeof v === "object" && v !== null && "nama" in v,
            );
          }

          // Pastikan selalu nge-set Array agar tidak undefined
          setIndikatorData(finalData);
        }
      })
      .catch((err) => console.error("Error load data:", err));
  }, [API]);

  // 🔥 PERBAIKAN 2: USE MEMO (ANTI UNDEFINED) 🔥
  const uniqueNames = useMemo(() => {
    // Jika indikatorData bukan array (mencegah error .map undefined), kembalikan array kosong
    if (!Array.isArray(indikatorData)) return [];
    return [
      ...new Set(indikatorData.map((item) => item.nama).filter(Boolean)),
    ].sort();
  }, [indikatorData]);

  const filteredNames = useMemo(() => {
    if (!form.nama || form.nama.length < 3) return [];
    return uniqueNames.filter((nama) =>
      nama.toLowerCase().includes(form.nama.toLowerCase()),
    );
  }, [form.nama, uniqueNames]);

  // ========================= FETCH SUBMITTED KPI =========================
  const fetchSubmittedKPI = async (namaKaryawan) => {
    try {
      const res = await axios.post(`${API}/api/kpi-submitted`, {
        email: user?.email,
        nama: namaKaryawan,
      });
      if (res.data.result === "success") {
        setSubmittedIndicators(res.data.data || []);
      }
    } catch (err) {
      console.error("Error load submitted KPI:", err);
    }
  };

  const handleNamaFocus = () => {
    setDropdownOpen(true);
  };

  const handleNamaChange = (e) => {
    const namaInput = e.target.value;
    setForm((prev) => ({ ...prev, nama: namaInput }));
    setSubmittedIndicators([]);
    setDropdownOpen(true);

    if (namaInput.trim() === "") {
      setForm((prev) => ({
        ...prev,
        divisi: "",
        unit: "",
        area_kinerja: "",
        parameter: "",
        sumber: "",
        indikator_kpi: "",
        target: "",
        bobot: "",
        nilai: "",
        actual: "",
      }));
    }
  };

  const handleSelectNama = (namaTerpilih) => {
    // Pastikan indikatorData berbentuk array sebelum dicari
    const amanIndikatorData = Array.isArray(indikatorData) ? indikatorData : [];
    const dataNama = amanIndikatorData.find((d) => d.nama === namaTerpilih);
    if (!dataNama) return;

    setForm({
      ...form,
      nama: dataNama.nama,
      divisi: dataNama.divisi,
      unit: dataNama.unit,
      area_kinerja: "",
      parameter: "",
      sumber: "",
      indikator_kpi: "",
      target: "",
      bobot: "",
      nilai: "",
      actual: "",
    });

    setDropdownOpen(false);
    fetchSubmittedKPI(namaTerpilih);

    const filtered = amanIndikatorData.filter((d) => d.nama === namaTerpilih);
    setFilteredIndicators(filtered);
  };

  const handleIndikatorChange = (e) => {
    const indikator = e.target.value;
    const data = filteredIndicators.find((d) => d.indikator_kpi === indikator);

    if (!data) {
      setForm({ ...form, indikator_kpi: indikator });
      setIsTargetFluktuatif(false);
      return;
    }

    const fluktuatif = checkIfTargetIsFluktuatif(data.target);
    setIsTargetFluktuatif(fluktuatif);

    setForm({
      ...form,
      indikator_kpi: data.indikator_kpi,
      area_kinerja: data.area_kinerja,
      parameter: data.parameter || "",
      sumber: data.sumber || "",
      target: data.target,
      bobot: formatBobot(data.bobot),
      nilai: data.nilai || "",
    });
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setBuktiIsPDF(false);

    if (!file) {
      setBuktiNilaiPreview(null);
      setBuktiNilaiName("");
      setBuktiNilaiFile(null);
      return;
    }

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      showModal(
        "Perhatian",
        "File harus berupa JPG, JPEG, PNG, atau PDF.",
        "error",
      );
      e.target.value = null;
      setBuktiNilaiPreview(null);
      setBuktiNilaiName("");
      setBuktiNilaiFile(null);
      return;
    }

    setBuktiNilaiName(file.name);
    setBuktiNilaiFile(file);

    if (file.type === "application/pdf") {
      setBuktiIsPDF(true);
      setBuktiNilaiPreview(URL.createObjectURL(file));
    } else {
      setBuktiNilaiPreview(URL.createObjectURL(file));
    }
  };

  const handleAddBatch = () => {
    if (!form.indikator_kpi || !form.actual) return;
    if (!buktiNilaiFile) {
      showModal("Perhatian", "Bukti nilai wajib diunggah.", "error");
      return;
    }

    const exists = batchList.some(
      (b) => b.indikator_kpi === form.indikator_kpi,
    );
    if (exists) {
      showModal("Perhatian", "Indikator sudah ditambahkan.", "error");
      return;
    }

    setBatchList((prev) => [
      ...prev,
      {
        ...form,
        bukti_nilai_name: buktiNilaiName || "",
        bukti_file_obj: buktiNilaiFile,
        bukti_pdf: buktiIsPDF ? true : false,
      },
    ]);

    setForm({
      ...form,
      indikator_kpi: "",
      area_kinerja: "",
      parameter: "",
      sumber: "",
      target: "",
      bobot: "",
      nilai: "",
      actual: "",
    });

    setBuktiNilaiPreview(null);
    setBuktiNilaiName("");
    setBuktiNilaiFile(null);
    setBuktiIsPDF(false);
    if (fileRef.current) fileRef.current.value = null;
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

const uploadSingleFileToGAS = async (file) => {
    let fileToUpload = file;
    if (file.type.startsWith("image/")) {
      try {
        const options = { maxSizeMB: 2, maxWidthOrHeight: 1920, useWebWorker: true };
        fileToUpload = await imageCompression(file, options);
      } catch (err) {
        console.warn("Gagal kompres gambar, upload file asli.", err);
      }
    }
    const base64Full = await fileToBase64(fileToUpload);
    const base64Data = base64Full.split(",")[1];
    
    // Pastikan URL GAS tidak kosong
    const URL_GAS = process.env.REACT_APP_GOOGLE_SCRIPT_ACTION_URL;
    if (!URL_GAS) throw new Error("URL Google Script belum diatur di .env");

    const payloadGAS = JSON.stringify({ action: "uploadBukti", fileBase64: base64Data, mimeType: file.type });
    const res = await axios.post(URL_GAS, payloadGAS, { headers: { "Content-Type": "text/plain" } });

    // 🔥 FIX UTAMA: Cara membaca kembalian buktiUrl dari Google Script 🔥
    if (res.data && res.data.result === "success") {
      // Ambil buktiUrl langsung, atau dari dalam message jika terbungkus
      return res.data.buktiUrl || (res.data.message && res.data.message.buktiUrl);
    } else {
      throw new Error(res.data?.message || "Gagal upload ke Google Drive");
    }
  };

  const handleSubmit = async () => {
    if (!form.nama || batchList.length === 0) {
      showModal(
        "Perhatian",
        "Pilih nama dan tambahkan minimal 1 indikator",
        "error",
      );
      return;
    }
    if (!drawing) {
      showModal("Perhatian", "Tanda tangan wajib diisi.", "error");
      return;
    }

    try {
      setLoading(true);
      setLoadingModal(true);
      setLoadingText("Sedang mengupload file bukti...");
      const finalKpiList = [];

      for (let i = 0; i < batchList.length; i++) {
        const item = batchList[i];
        setLoadingText(`Mengupload file ${i + 1} dari ${batchList.length}...`);
        let urlBukti = "";
        if (item.bukti_file_obj) {
          try {
            urlBukti = await uploadSingleFileToGAS(item.bukti_file_obj);
          } catch (err) {
            throw new Error(`Gagal upload bukti untuk: ${item.indikator_kpi}.`);
          }
        }
        const actualClean = item.actual.toString().replace(",", ".");
        finalKpiList.push({
          ...item,
          actual: actualClean,
          bukti_nilai: urlBukti,
          bukti_file_obj: undefined,
        });
      }

      setLoadingText("Menyimpan data KPI ke server...");
      const payload = {
        email: user.email,
        password: user.password,
        nama: form.nama,
        divisi: form.divisi,
        unit: form.unit,
        tanda_tangan: drawing,
        indikator_list: finalKpiList,
        is_new_user: false,
      };

      const res = await axios.post(`${API}/api/kpi-batch`, payload);
      setLoadingModal(false);

      if (res.data.result === "success") showFinishModal();
      else showModal("Gagal", res.data.message, "error");
    } catch (err) {
      setLoadingModal(false);
      const serverMsg = err.response?.data?.message
        ? ` — ${err.response.data.message}`
        : "";
      showModal(
        "Gagal",
        "Error input KPI: " + err.message + serverMsg,
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  // ===== SIGNATURE =====
  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setDrawing("");
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";

    let rect = canvas.getBoundingClientRect();
    let x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    let y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);

    const move = (ev) => {
      let nx = (ev.touches ? ev.touches[0].clientX : ev.clientX) - rect.left;
      let ny = (ev.touches ? ev.touches[0].clientY : ev.clientY) - rect.top;
      ctx.lineTo(nx, ny);
      ctx.stroke();
    };

    const up = () => {
      setDrawing(canvas.toDataURL());
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", up);
    };

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchmove", move);
    window.addEventListener("touchend", up);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resizeCanvas = () => {
      const ratio = window.devicePixelRatio || 1;
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      const ctx = canvas.getContext("2d");
      ctx.scale(ratio, ratio);
      if (drawing) {
        const img = new Image();
        img.src = drawing;
        img.onload = () =>
          ctx.drawImage(img, 0, 0, canvas.offsetWidth, canvas.offsetHeight);
      }
    };
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, [drawing]);

  const checkIfTargetIsFluktuatif = (target) =>
    typeof target === "string" && target.toLowerCase().includes("fluktuatif");

  const isIndikatorSelesai = (item) => {
    if (!item || !item.indikator_kpi) return false;

    const inBatch = batchList.some(
      (b) => b.indikator_kpi === item.indikator_kpi,
    );
    if (inBatch) return true;

    if (!item.divisi || !item.area_kinerja) return false;

    const keyToMatch = [
      String(item.divisi).trim(),
      String(item.area_kinerja).trim(),
      String(item.indikator_kpi).trim(),
    ]
      .join("|")
      .toLowerCase();

    return submittedIndicators.includes(keyToMatch);
  };

  const handleActualChange = (e) => {
    let val = e.target.value.replace(".", ",");
    if (val === "" || /^[0-9,]*$/.test(val)) {
      if ((val.match(/,/g) || []).length <= 1)
        setForm({ ...form, actual: val });
    }
  };

  const formatBobot = (val) => {
    if (!val && val !== 0) return "";
    const num = Number(val);
    if (!isNaN(num) && num > 0 && num <= 1) {
      return Math.round(num * 100).toString();
    }
    return String(val).replace("%", "");
  };

  return (
    <>
      <div className="rsia-modal-backdrop">
        <div
          className="rsia-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="kpi-title"
        >
          <div className="rsia-modal-header">
            <h5 id="kpi-title">Input Aktual KPI</h5>
            <button
              className="rsia-modal-close"
              onClick={onClose}
              disabled={loading}
            >
              &times;
            </button>
          </div>

          <div className="rsia-modal-body">
            {/* ===================== NAMA KARYAWAN ===================== */}
            <div
              className="rsia-form-group"
              style={{ position: "relative" }}
              ref={namaDropdownRef}
            >
              <label>Nama Karyawan</label>
              <input
                ref={inputRef}
                name="nama"
                value={form.nama}
                onChange={handleNamaChange}
                onFocus={handleNamaFocus}
                placeholder="Ketik minimal 3 huruf..."
                autoComplete="off"
                disabled={loading}
              />

              {dropdownOpen && (
                <ul className="rsia-dropdown" role="listbox">
                  {form.nama.length < 3 ? (
                    <li className="not-found-state">
                      <div
                        className="not-found-text"
                        style={{
                          color: "#64748b",
                          fontStyle: "italic",
                          fontSize: "0.9rem",
                        }}
                      >
                        Ketik minimal 3 huruf untuk memunculkan daftar nama...
                      </div>
                    </li>
                  ) : filteredNames.length > 0 ? (
                    filteredNames.map((nama, index) => (
                      <li key={index} onClick={() => handleSelectNama(nama)}>
                        <span className="rsia-nama">{nama}</span>
                        <small className="rsia-detail">
                          {Array.isArray(indikatorData)
                            ? indikatorData.find((d) => d.nama === nama)?.divisi
                            : ""}{" "}
                          -{" "}
                          {Array.isArray(indikatorData)
                            ? indikatorData.find((d) => d.nama === nama)?.unit
                            : ""}
                        </small>
                      </li>
                    ))
                  ) : (
                    <li className="not-found-state">
                      <div className="not-found-text">
                        Data <b>"{form.nama}"</b> tidak ditemukan di Master
                        Data.
                        <br />
                        <small style={{ color: "#64748b" }}>
                          Gunakan tombol "Tambah Indikator Baru" di Halaman
                          Utama.
                        </small>
                      </div>
                    </li>
                  )}
                </ul>
              )}
            </div>

            {/* ===================== FORM DATA ===================== */}
            <div className="rsia-form-row">
              <div className="rsia-form-group">
                <label>Divisi</label>
                <input
                  name="divisi"
                  value={form.divisi}
                  onChange={handleChange}
                  placeholder="Divisi"
                  readOnly
                  className="rsia-input-locked"
                />
              </div>
              <div className="rsia-form-group">
                <label>Unit</label>
                <input
                  name="unit"
                  value={form.unit}
                  onChange={handleChange}
                  placeholder="Unit"
                  readOnly
                  className="rsia-input-locked"
                />
              </div>
            </div>

            <div className="rsia-form-group" style={{ position: "relative" }}>
              <label>Indikator KPI</label>
              <>
                <div
                  className={`rsia-premium-select ${indikatorOpen ? "open" : ""} ${!form.nama ? "rsia-input-locked" : ""}`}
                  onClick={() => {
                    if (form.nama) setIndikatorOpen((o) => !o);
                  }}
                >
                  <span className={form.indikator_kpi ? "" : "placeholder"}>
                    {form.indikator_kpi || "Pilih Indikator yang akan diisi..."}
                  </span>
                  <span className="arrow">▾</span>
                </div>

                {indikatorOpen && (
                  <ul
                    className="rsia-premium-dropdown kpi-dropdown-modern"
                    role="listbox"
                  >
                    {filteredIndicators.map((item, index) => {
                      const selesai = isIndikatorSelesai(item);
                      const cleanKpiName = String(item.indikator_kpi)
                        .replace(/\s*\([^)]*\)\s*$/, "")
                        .trim();

                      return (
                        <li
                          key={index}
                          className={`kpi-modern-item ${selesai ? "done" : ""}`}
                          onClick={() => {
                            if (selesai) return;
                            handleIndikatorChange({
                              target: { value: item.indikator_kpi },
                            });
                            setIndikatorOpen(false);
                          }}
                        >
                          <div className="kpi-item-content">
                            <span
                              className="kpi-title"
                              style={{
                                fontWeight: "normal",
                                color: "#334155",
                                fontSize: "0.9rem",
                                display: "block",
                              }}
                            >
                              {cleanKpiName}
                            </span>
                          </div>
                          {selesai && (
                            <div className="kpi-done-indicator">
                              <span className="done-badge">✓ Selesai</span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </>
            </div>

            <div className="rsia-form-row">
              <div className="rsia-form-group">
                <label>Area Kinerja</label>
                <input
                  name="area_kinerja"
                  value={form.area_kinerja}
                  onChange={handleChange}
                  placeholder="Area Kinerja"
                  readOnly
                  className="rsia-input-locked"
                />
              </div>
              <div className="rsia-form-group">
                <label>Parameter</label>
                <input
                  name="parameter"
                  value={form.parameter}
                  onChange={handleChange}
                  placeholder="Parameter"
                  readOnly
                  className="rsia-input-locked"
                />
              </div>
            </div>

            <div className="rsia-form-row">
              <div className="rsia-form-group">
                <label>Sumber</label>
                <input
                  name="sumber"
                  value={form.sumber}
                  onChange={handleChange}
                  placeholder="Sumber"
                  readOnly
                  className="rsia-input-locked"
                />
              </div>

              <div className="rsia-form-group">
                <label>Bobot (%)</label>
                <input
                  name="bobot"
                  type="number"
                  value={form.bobot}
                  onChange={handleChange}
                  placeholder="Bobot"
                  readOnly
                  className="rsia-input-locked"
                />
              </div>
            </div>

            {/* Target dan Actual sekarang disatukan di dalam rsia-form-row agar bersebelahan */}
            <div className="rsia-form-row">
              <div className="rsia-form-group">
                <label>
                  Target
                  {isTargetFluktuatif && (
                    <>
                      <input
                        type="checkbox"
                        checked={editTarget}
                        onChange={(e) => setEditTarget(e.target.checked)}
                        style={{ marginLeft: 10 }}
                      />
                      <span style={{ marginLeft: 5 }}>Ubah Target</span>
                    </>
                  )}
                </label>
                <input
                  name="target"
                  placeholder="Nilai Target"
                  value={form.target}
                  onChange={handleChange}
                  readOnly={!isTargetFluktuatif || !editTarget}
                  disabled={loading}
                  className={
                    !isTargetFluktuatif || !editTarget
                      ? "rsia-input-locked"
                      : ""
                  }
                />
              </div>

              <div className="rsia-form-group">
                <label>Actual</label>
                <input
                  type="text"
                  name="actual"
                  value={form.actual}
                  placeholder="Ketik nilai aktual..."
                  onChange={handleActualChange}
                  disabled={loading || !form.indikator_kpi}
                  inputMode="decimal"
                />
              </div>
            </div>

            {/* ===================== UPLOAD & BATCH ===================== */}
            <div className="rsia-form-group">
              <label>Bukti Nilai (jpg/png/pdf)</label>
              <input
                type="file"
                ref={fileRef}
                onChange={handleFileChange}
                disabled={loading || !form.indikator_kpi}
              />
              {buktiNilaiPreview && !buktiIsPDF && (
                <div className="rsia-preview-wrap">
                  <div className="rsia-preview-title">
                    Preview ({buktiNilaiName})
                  </div>
                  <img
                    src={buktiNilaiPreview}
                    alt="preview"
                    className="rsia-preview"
                  />
                </div>
              )}
              {buktiIsPDF && (
                <div
                  className="rsia-preview-wrap"
                  style={{ marginTop: "10px" }}
                >
                  <div className="rsia-preview-title">
                    File PDF: {buktiNilaiName}
                  </div>
                  <iframe
                    src={buktiNilaiPreview}
                    title="PDF Preview"
                    style={{
                      width: "100%",
                      height: "400px",
                      border: "1px solid #ccc",
                    }}
                  />
                </div>
              )}
            </div>

            <div className="rsia-form-group">
              <button
                type="button"
                className="add-batch"
                onClick={handleAddBatch}
                disabled={!form.indikator_kpi || !form.actual || loading}
              >
                Tambah ke Daftar Antrean
              </button>
            </div>

            {batchList.length > 0 && (
              <div className="rsia-batch-list">
                <label>Daftar Indikator Siap Kirim</label>
                <ul>
                  {batchList.map((b, i) => (
                    <li key={i}>
                      <div className="batch-info">
                        <div className="indikator">{b.indikator_kpi}</div>
                        <div className="meta">
                          Actual: {b.actual} • Bukti:{" "}
                          {b.bukti_nilai_name || "—"} {b.bukti_pdf && " (PDF)"}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="delete-batch"
                        onClick={() =>
                          setBatchList((prev) =>
                            prev.filter((_, idx) => idx !== i),
                          )
                        }
                        disabled={loading}
                      >
                        Hapus
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rsia-form-group">
              <label className="rsia-sign-label">Tanda Tangan</label>
              <canvas
                ref={canvasRef}
                width={600}
                height={200}
                onMouseDown={startDrawing}
                onTouchStart={startDrawing}
                style={{
                  width: "100%",
                  height: "200px",
                  touchAction: "none",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                }}
              />
              <div className="rsia-canvas-footer">
                <button
                  type="button"
                  className="clear-sign"
                  onClick={clearSignature}
                  disabled={loading}
                >
                  Hapus Tanda Tangan
                </button>
              </div>
            </div>
          </div>

          <div className="rsia-modal-footer">
            <button onClick={onClose} disabled={loading}>
              Batal
            </button>
            <button onClick={handleSubmit} disabled={loading}>
              Simpan KPI
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={modalOpen}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
        onClose={() => {
          setModalOpen(false);
          if (modalType === "finish") onClose();
        }}
      />
      <Modal
        isOpen={loadingModal}
        title="Menyimpan Data"
        message={loadingText}
        type="loading"
        isLoading={true}
        onClose={null}
      />
    </>
  );
}
